import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import Table from '@lmc/ui-kit/src/molecules/Table/Table'
import dayjs from 'dayjs'
import ResidentCell from '@lmc/ui-kit/src/molecules/Table/cells/ResidentCell'
import { TablePaginationConfig } from 'antd'
import { TableParams } from '../../types'
import { getLocationItemType } from '../../utils'
import useCarers, { getCarer } from 'hooks/useFindItem/useCarers'
import { DeviceTrailListResponse } from '@lmc/ts-common'
import { ColumnsType } from 'antd/es/table'

type DevicesSignsTableProps = {
  signs?: DeviceTrailListResponse[]
  isLoading?: boolean
  tableParams: TableParams
  handleTableChange: (pagination: TablePaginationConfig) => void
  testId?: string
}

type DataSource = {
  deviceName: string
  carer: string
  location: {
    type: DeviceTrailListResponse['type']
    signIn: DeviceTrailListResponse['signIn']
  }
  timestamp: string
  key: string
}

const DevicesSignsTable: FC<DevicesSignsTableProps> = ({
  signs,
  isLoading,
  tableParams,
  handleTableChange,
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const rows: DataSource[] | undefined = signs?.map((sign) => ({
    deviceName: sign?.name,
    carer: sign?.user.id,
    location: {
      type: sign?.type,
      signIn: sign?.signIn,
    },
    timestamp: dayjs(sign.timestamp).format('HH:mm DD/MM/YYYY '),
    key: sign?.id,
  }))

  const { all: carers } = useCarers({})

  const hasPagination: object | false =
    tableParams?.pagination?.total && tableParams.pagination.total > 0
      ? tableParams.pagination
      : false

  const columns: ColumnsType<DataSource> = [
    {
      dataIndex: 'timestamp',
      title: t(dacConstants.SIGN_IN),
      sortDirections: ['descend', 'ascend'],
      sorter: true,
    },
    {
      dataIndex: 'deviceName',
      title: t(dacConstants.DEVICE_NAME),
      sortDirections: ['descend', 'ascend'],
      sorter: true,
    },
    {
      dataIndex: 'carer',
      title: t(dacConstants.TEAM_MEMBER),
      render: (id: string) => {
        const user = getCarer(carers, id)
        return (
          user && (
            <ResidentCell
              avatarSize={30}
              resident={{
                name: user.name,
                picture: user.picture as unknown as string,
              }}
            />
          )
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: true,
    },
    {
      dataIndex: 'location',
      title: t(dacConstants.LOCATION),
      render: ({ type, signIn }) => getLocationItemType(type, t, signIn),
    },
  ]

  return (
    <Table
      onChange={handleTableChange}
      pagination={hasPagination}
      loading={isLoading}
      columns={columns}
      dataSource={rows}
      data-testid={testId}
    />
  )
}

export default DevicesSignsTable
